import { SOLVE_PHASES } from "@playbookai/types";
import { parseSolvePhaseAreaGeometry } from "@playbookai/widget-schemas";
import * as Y from "yjs";
import { type SolvePhase, type WidgetKind } from "@playbookai/types";
import { widgetsMap } from "@playbookai/collab-client";
import { getWidget, stickyNotesMap, type Rect } from "@playbookai/ui";
import type {
  PhaseAreaCarry,
  PhaseAreaEntries,
  PhaseAreaEntry,
  PhaseAreaResizePlan,
} from "./phase-areas.js";

/**
 * Feature-local Yjs doc-shape for SOLVE's resizable phase areas.
 *
 * This module is the ONLY place that reaches for `doc.getMap('solvePhaseAreas')`,
 * mirroring `vsm-feature`'s `artifact-doc-shape.ts` and `hoshin-feature`'s
 * `prioritize-doc-shape.ts` — the established pattern for a feature package
 * owning one top-level key on its own canvas doc.
 *
 * **One nested `Y.Map` per phase**, keyed by the phase name, carrying exactly
 * `{ x, w, h }`. Nested rather than flat so two people resizing two different
 * areas never tombstone each other's entry, and exactly those three fields so
 * the phase's label, colour and subtitle keep deriving from `PHASE_META` —
 * recolouring or relabelling a phase needs no migration.
 *
 * The key is additive and lazily materialised: an empty `Y.Map` contributes
 * nothing to encoded doc state, `ensureCanvasShape` does not force-seed it, and
 * nothing branches on `CANVAS_SCHEMA_VERSION` — the same discipline that let
 * VSM's `kind:'container'` and `vsmArtifacts` land without a version bump. A
 * canvas nobody has resized stores nothing at all and renders exactly the five
 * fixed lanes it always did.
 *
 * Geometry is Yjs-only. Backend placement projections are derived from
 * Collaboration Service updates (ADR-0004), never dual-written from here — the
 * same rule `ensureSolveWidgetLayout` states, and the posture VSM's phase
 * containers and swimlanes already have.
 */

export type PhaseAreaMap = Y.Map<Y.Map<unknown>>;
const PHASE_SET: ReadonlySet<string> = new Set(SOLVE_PHASES);

/**
 * Sticky-note default width, for the centre test on a note whose own `w` is
 * missing. `addStickyNote` always writes one, so this only covers a hand-made
 * or future-shaped entry; the DS does not export its constant.
 */
const DEFAULT_NOTE_WIDTH = 220;

/** The phase-area container. Lazily materialised like every other top-level map. */
export function phaseAreasMap(doc: Y.Doc): PhaseAreaMap {
  return doc.getMap<Y.Map<unknown>>("solvePhaseAreas");
}

/**
 * Every stored area. Reads defensively per FIELD, not per entry: a partial
 * entry written by a future or older client contributes nothing rather than
 * poisoning the layout with `NaN`.
 */
export function readPhaseAreas(doc: Y.Doc): PhaseAreaEntries {
  const values: Record<string, unknown> = {};
  phaseAreasMap(doc).forEach((entry, key) => {
    if (entry instanceof Y.Map) values[key] = entry.toJSON();
  });
  return parseSolvePhaseAreaGeometry(values);
}

/**
 * Write an area's rect. Returns whether anything changed.
 *
 * Every field is guarded on an actual change. `Y.Map.set` creates a new item
 * and tombstones the old one even for an identical primitive, so an
 * unconditional write would broadcast an update to every peer on each
 * idempotent commit — the failure VSM recorded when an already-tidy board
 * emitted one update per container while correctly reporting no change.
 */
export function writePhaseAreaRect(
  doc: Y.Doc,
  phase: SolvePhase,
  rect: PhaseAreaEntry,
): boolean {
  if (!rectDiffers(phaseAreasMap(doc), phase, rect)) return false;
  doc.transact(() => {
    setRectInTransaction(phaseAreasMap(doc), phase, rect);
  });
  return true;
}

/**
 * Apply a whole resize — the area's new size, every area the push moves, and the
 * content that travels with them — in ONE transaction.
 *
 * One transaction is the load-bearing part. Peers and the local reader both
 * derive the areas and the widgets from the same document, so a two-step write
 * would broadcast an intermediate state in which the areas had moved and their
 * contents had not. VSM's `extendMapForRail` sets the same precedent: the lane
 * grows and every symbol shifts in a single `doc.transact`.
 *
 * Returns the canvas keys of the widgets that moved. The caller re-persists each
 * through the ordinary placement upsert, which is what keeps
 * `widget_placements.position` from going stale — Player AI reads those rows to
 * avoid overlap, and the placement gate is where a carried widget's phase lock is
 * enforced and its `widget_placement_moved` audit row written.
 */
export function applyPhaseAreaResize(
  doc: Y.Doc,
  plan: PhaseAreaResizePlan,
): { changed: boolean; movedWidgetIds: readonly string[] } {
  let changed = false;
  let movedWidgetIds: readonly string[] = [];
  doc.transact(() => {
    const map = phaseAreasMap(doc);
    for (const [phase, rect] of plan.rects) {
      if (!rectDiffers(map, phase, rect)) continue;
      setRectInTransaction(map, phase, rect);
      changed = true;
    }
    if (changed && plan.carry.dx !== 0) {
      movedWidgetIds = carryCanvasContent(doc, plan.carry);
      if (movedWidgetIds.length > 0) changed = true;
    }
  });
  return { changed, movedWidgetIds };
}

/**
 * Shift the widgets and notes that belong to the pushed areas by the same
 * offset. Only `x` moves: widget sizes and every other field are untouched, so
 * relative layout inside each area survives exactly.
 *
 * Membership is split the way VSM splits a container's occupants. A widget with
 * a STORED phase travels declaratively — it belongs to that area even if someone
 * dragged it elsewhere, which is the same authority the floor and the lock gate
 * read. A sticky note and a phase-agnostic widget have no phase to travel by, so
 * their membership is geometric: they travel when their CENTRE is at or past the
 * resized area's old right edge.
 */
function carryCanvasContent(
  doc: Y.Doc,
  carry: PhaseAreaCarry,
): readonly string[] {
  const travellingPhases = new Set<string>(carry.phases);
  const movedWidgetIds: string[] = [];
  const shift = (entry: Y.Map<unknown>): boolean => {
    const x = finite(entry.get("x"));
    if (x === null) return false;
    entry.set("x", x + carry.dx);
    return true;
  };
  widgetsMap(doc).forEach((entry, key) => {
    const phase = entry.get("phase");
    if (typeof phase === "string" && travellingPhases.has(phase)) {
      if (shift(entry)) movedWidgetIds.push(key);
      return;
    }
    // A widget that belongs to the resized area, or to one before it, stays put.
    if (typeof phase === "string" && PHASE_SET.has(phase)) return;
    const def = getWidget(entry.get("kind") as WidgetKind);
    const width = positive(entry.get("w")) ?? def?.defaultSize.w ?? 220;
    if (centreAtOrPast(entry, width, carry.fromX) && shift(entry)) {
      movedWidgetIds.push(key);
    }
  });
  // Sticky notes are Yjs-only — no widget row, no placement — so they travel
  // here and nowhere else. Nothing to re-persist for them.
  stickyNotesMap(doc).forEach((note) => {
    const width = positive(note.get("w")) ?? DEFAULT_NOTE_WIDTH;
    if (centreAtOrPast(note, width, carry.fromX)) shift(note);
  });
  return movedWidgetIds;
}

function centreAtOrPast(
  entry: Y.Map<unknown>,
  width: number,
  boundary: number,
): boolean {
  const x = finite(entry.get("x"));
  return x !== null && x + width / 2 >= boundary;
}

function rectDiffers(
  map: PhaseAreaMap,
  phase: SolvePhase,
  rect: PhaseAreaEntry,
): boolean {
  const existing = map.get(phase);
  return !(
    existing instanceof Y.Map &&
    existing.get("x") === rect.x &&
    existing.get("w") === rect.w &&
    existing.get("h") === rect.h
  );
}

function setRectInTransaction(
  map: PhaseAreaMap,
  phase: SolvePhase,
  rect: PhaseAreaEntry,
): void {
  const current = map.get(phase);
  if (!(current instanceof Y.Map)) {
    const next = new Y.Map<unknown>();
    next.set("x", rect.x);
    next.set("w", rect.w);
    next.set("h", rect.h);
    map.set(phase, next);
    return;
  }
  if (current.get("x") !== rect.x) current.set("x", rect.x);
  if (current.get("w") !== rect.w) current.set("w", rect.w);
  if (current.get("h") !== rect.h) current.set("h", rect.h);
}

/**
 * Place an area that has never been placed. Returns whether it wrote.
 *
 * Re-checks inside the transaction because another replica's update may land
 * between the guard and the write — the phase name doubles as a convergent CRDT
 * key, so two clients that watch the same reveal converge on one entry rather
 * than two.
 */
export function materializePhaseArea(
  doc: Y.Doc,
  phase: SolvePhase,
  rect: PhaseAreaEntry,
): boolean {
  const map = phaseAreasMap(doc);
  if (map.has(phase)) return false;
  let wrote = false;
  doc.transact(() => {
    if (map.has(phase)) return;
    const next = new Y.Map<unknown>();
    next.set("x", rect.x);
    next.set("w", rect.w);
    next.set("h", rect.h);
    map.set(phase, next);
    wrote = true;
  });
  return wrote;
}

/**
 * The rects of every widget assigned to `phase`, for the contents floor.
 *
 * Membership is the widget's STORED `phase` — the field the placement upsert
 * owns and the lock gate reads (ADR-0019) — so a widget dragged across a
 * boundary still keeps the area it belongs to from shrinking past it. Falls
 * back to the registered default size when a ref carries no `w`/`h`, matching
 * how `reserveManualWidgetPosition` reads the same refs.
 */
export function phaseWidgetRects(doc: Y.Doc, phase: SolvePhase): Rect[] {
  const rects: Rect[] = [];
  widgetsMap(doc).forEach((ymap) => {
    if (ymap.get("phase") !== phase) return;
    const x = finite(ymap.get("x"));
    const y = finite(ymap.get("y"));
    if (x === null || y === null) return;
    const def = getWidget(ymap.get("kind") as WidgetKind);
    rects.push({
      x,
      y,
      w: positive(ymap.get("w")) ?? def?.defaultSize.w ?? 220,
      h: positive(ymap.get("h")) ?? def?.defaultSize.h ?? 150,
    });
  });
  return rects;
}

function finite(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function positive(value: unknown): number | null {
  const numeric = finite(value);
  return numeric !== null && numeric > 0 ? numeric : null;
}
